import { useEffect } from 'react';
import type { MediaDeviceInfo } from '../useMicrophone/useMicrophone';
import type { RecoilState } from 'recoil';

import { atom, useRecoilValue, useSetRecoilState } from 'recoil';
import { deviceListFingerprint } from 'utils/mediaDeviceKeys';

type UseMediaDevicesState = {
  devices: MediaDeviceInfo[];
  loaded: boolean;
};

export const mediaDevicesState: RecoilState<UseMediaDevicesState> = atom<UseMediaDevicesState>({
  key: 'mediaDevicesState',
  default: {
    devices: [],
    loaded: false,
  },
});

type DeviceListSubscriber = (devices: MediaDeviceInfo[]) => void;

// The `devicechange` registration is module-level and ref-counted rather than owned by the
// first consumer to mount. Recorder subtrees mount and unmount routinely; when the listener
// left with the first consumer, every later device change went unnoticed for the rest of the
// session — which is what made an unplugged dictaphone unrecoverable (RP-6018).
const subscribers = new Set<DeviceListSubscriber>();
let listeningTo: MediaDevices | null = null;
let deviceReadRevision = 0;

const readDevices = async (): Promise<MediaDeviceInfo[] | null> => {
  const { mediaDevices } = navigator;
  if (mediaDevices == null) return null;

  return mediaDevices
    .enumerateDevices()
    .then((devices) =>
      devices.map(({ deviceId, groupId, kind, label }) => ({ deviceId, groupId, kind, label }))
    )
    .catch(() => null);
};

const broadcastDevices = () => {
  const revision = ++deviceReadRevision;
  readDevices().then((devices) => {
    // An older enumeration must not overwrite the result of a newer device change.
    if (devices == null || revision !== deviceReadRevision) return;
    subscribers.forEach((subscriber) => subscriber(devices));
  });
};

const subscribeToDeviceChanges = (subscriber: DeviceListSubscriber): (() => void) => {
  subscribers.add(subscriber);

  const { mediaDevices } = navigator;
  if (mediaDevices != null && listeningTo !== mediaDevices) {
    listeningTo?.removeEventListener('devicechange', broadcastDevices);
    mediaDevices.addEventListener('devicechange', broadcastDevices);
    listeningTo = mediaDevices;
  }

  return () => {
    subscribers.delete(subscriber);
    if (subscribers.size > 0) return;
    listeningTo?.removeEventListener('devicechange', broadcastDevices);
    listeningTo = null;
  };
};

export const useMediaDevices = (): UseMediaDevicesState => {
  const state = useRecoilValue(mediaDevicesState);
  const setState = useSetRecoilState(mediaDevicesState);

  useEffect(() => {
    // Reading current state through the updater rather than a mount-time closure: the previous
    // implementation compared against the state captured on first render, which was always
    // empty, so every event replaced the array whether or not anything had changed.
    const applyDevices = (devices: MediaDeviceInfo[]) => {
      setState((current) => {
        if (
          current.loaded &&
          deviceListFingerprint(current.devices) === deviceListFingerprint(devices)
        ) {
          return current;
        }
        return { devices, loaded: true };
      });
    };

    const unsubscribe = subscribeToDeviceChanges(applyDevices);
    broadcastDevices();

    return unsubscribe;
  }, [setState]);

  return state;
};
